import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import {
  RotateCcw,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useHlsPlayer } from "@/hooks/useHlsPlayer";
import { useLatest } from "@/hooks/useLatest";
import { useVideoPlaybackSource } from "@/hooks/useVideoPlaybackSource";
import { VideoControlBar } from "@/components/video/controls/VideoControlBar";
import type { QualityMenuState } from "@/components/video/controls/SettingsMenu";
import { CollapseButton } from "@/components/video/CollapseButton";
import { VideoSourceSelect } from "@/components/video/VideoSourceSelect";
import {
  useBufferedEnd,
  useFullscreen,
  usePictureInPicture,
  usePlaybackRate,
} from "@/components/video/controls/useMediaElement";
import { VideoTelemetrySyncOffset } from "@/components/sync/VideoTelemetrySyncOffset";
import { getVideoCursorState } from "@/utils/sharedTimeline";
import {
  buildRecordingMediaTimeline,
  getRecordingMediaTime,
  getRecordingSessionTime,
} from "@/utils/recordingMediaTimeline";
import {
  getNextVideoSessionClip,
  getVideoLocalTime,
  getVideoSessionClipAtTime,
  getVideoSessionTime,
  TIMELINE_EPSILON_SECONDS,
  type VideoSessionTimeline,
} from "@/utils/videoSessionTimeline";
import type {
  SessionRecordingSummary,
  VideoFile,
} from "@/types";

// Sync-policy thresholds for the shared-cursor follow behaviour.
const DRIFT_THRESHOLD_S = 0.5;const RETRY_SEEK_TOLERANCE_S = 1;
const RETRY_DELAY_MS = 500;
// Deep-link intent TTL: user interactions shortly after the one-shot apply
// must stay authoritative even if the cursor never reaches the deep-linked
// clip (hidden-tab timer throttling eats into this margin — do not lower).
const DEEP_LINK_INTENT_TTL_MS = 2_000;
const UNEXPECTED_RESET_MIN_INTENDED_S = 10;
const RESET_RESTORE_DELAY_MS = 100;
const SEEK_JUMP_THRESHOLD_S = 5;
const PROPAGATION_JUMP_S = 2;
const PROGRESSIVE_UPDATE_TOLERANCE_S = 2;

const formatBitrate = (bitrate: number): string => {
  if (bitrate >= 1_000_000) {
    const megabits = bitrate / 1_000_000;
    return `${megabits >= 10 ? megabits.toFixed(0) : megabits.toFixed(1)} Mbps`;
  }
  return `${Math.round(bitrate / 1_000)} kbps`;
};

interface SynchronizedVideoPlayerProps {
  videos: VideoFile[];
  recordings?: SessionRecordingSummary[];
  currentTime?: number; // Current telemetry time in seconds
  onTimeUpdate?: (time: number) => void; // Callback when video time changes
  onVideoSelect?: (videoId: string) => void;
  // Seeds the initial selection (e.g. a choice restored from persisted
  // settings). Read once, at mount: afterwards the selection is owned here
  // and reported upward through onVideoSelect. Ignored once the id is no
  // longer selectable, and outranked by the timeline clip under the cursor.
  initialVideoId?: string;
  // One-shot deep-link selection (issue #1000): applied the first time the
  // id becomes selectable, never again afterwards — manual selections win
  // from then on, even while the prop keeps holding the deep-linked id.
  requestedVideoId?: string;
  onDurationChange?: (videoId: string, duration: number) => void;
 className?: string;
  variant?: "default" | "compact";
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  // Video control functions
  onGetVideoControls?: (controls: {
    togglePlayPause: () => void;
    pause: () => void;
    toggleMute?: () => void;
    toggleFullscreen?: () => void;
  }) => void;
  // Volume control
  volume?: number;
  onVolumeChange?: (volume: number) => void;
  // Sync controls
  syncOffset?: number;
  onSyncOffsetChange?: (offset: number) => void;
  videoCreatedAt?: string;
  telemetryStartTime?: string;
  telemetryTimestampSource?: import("@/hooks/useTelemetryTimestamp").TelemetryTimestampSource;
  timeline?: VideoSessionTimeline | null;
  recordingPlaybackByVideoId?: Record<string, SessionRecordingSummary>;
  onRefreshRecordingPlayback?: (recordingId: string) => Promise<string | null>;
  // Video-only layout: size the video to the available container height
  // instead of the default max-h-80 cap.
  fillAvailableSpace?: boolean;
}

export default function SynchronizedVideoPlayer({
  videos,
  recordings,
  currentTime = 0,
  onTimeUpdate,
  onVideoSelect,
  initialVideoId,
  requestedVideoId,
  onDurationChange,
 className = "",
  variant = "default",
  isCollapsed = false,
  onToggleCollapse,
  onGetVideoControls,
  volume = 0.8,
  onVolumeChange,
  syncOffset = 0,
  onSyncOffsetChange,
  videoCreatedAt,
  telemetryStartTime,
  telemetryTimestampSource,
  timeline = null,
  recordingPlaybackByVideoId = {},
  onRefreshRecordingPlayback,
  fillAvailableSpace = false,
}: SynchronizedVideoPlayerProps) {
  const { t } = useTranslation(["pages", "common"]);
  const isCompact = variant === "compact";
  const videoRef = useRef<HTMLVideoElement>(null);
  // Fullscreen targets this shell rather than the <video>, so the control bar
  // and the recording-gap overlay stay composited over the picture.
  const playerShellRef = useRef<HTMLDivElement>(null);
  const [selectedVideoId, setSelectedVideoId] = useState<string>(
    initialVideoId ?? "",
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(volume === 0);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const bufferedEnd = useBufferedEnd(videoRef);
  const { isFullscreen, toggleFullscreen } = useFullscreen(
    playerShellRef,
    videoRef,
  );
  const pip = usePictureInPicture(videoRef);
  const { playbackRate, setPlaybackRate } = usePlaybackRate(videoRef);
  const [showControls, setShowControls] = useState(true);
  const [isUserSeeking, setIsUserSeeking] = useState(false);
  const [isDraggingScrubber, setIsDraggingScrubber] = useState(false);
  const [initialMousePos, setInitialMousePos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [lastSyncTime, setLastSyncTime] = useState<number>(currentTime);
  const [isSyncControlsCollapsed, setIsSyncControlsCollapsed] = useState(true);
  const [pendingSeekTime, setPendingSeekTime] = useState<number | null>(null);
  const [intendedTime, setIntendedTime] = useState<number>(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  // All render sites pre-filter to completed uploads, so every video here is
  // selectable.
  const availableVideosById = useMemo(
    () => new Map(videos.map((video) => [video.id, video])),
    [videos],
  );
  const recordingSelectionOptions = useMemo(
    () =>
      (recordings ?? []).flatMap((recording, recordingIndex) => {
        const availableChapters = recording.chapters.filter((chapter) =>
          availableVideosById.has(chapter.id),
        );
        const videoIds = availableChapters.map((chapter) => chapter.id);
        if (videoIds.length === 0) {
          return [];
        }
        return [
          {
            id: recording.id,
            // Preserve the source filename as the visible identity. The
            // recording remains the selection/playback unit; its remaining
            // chapter filenames stay available in the expanded file list.
            label:
              availableChapters[0]?.filename ??
              t("sessions.detail.videoFiles.recordingLabel", {
                index: recordingIndex + 1,
              }),
            videoIds,
          },
        ];
      }),
    [availableVideosById, recordings, t],
  );
  const isRecordingSelection = recordings !== undefined;
  const selectableVideoIds = useMemo(
    () =>
      new Set(
        isRecordingSelection
          ? recordingSelectionOptions.flatMap((recording) => recording.videoIds)
          : videos.map((video) => video.id),
      ),
    [isRecordingSelection, recordingSelectionOptions, videos],
  );
  const resolvedRecordingPlaybackByVideoId = useMemo(() => {
    const resolved = { ...recordingPlaybackByVideoId };
    for (const recording of recordings ?? []) {
      for (const chapter of recording.chapters) {
        resolved[chapter.id] = recording;
      }
    }
    return resolved;
  }, [recordingPlaybackByVideoId, recordings]);
  const selectedVideo = videos.find((v) => v.id === selectedVideoId);
  const selectedRecording =
    resolvedRecordingPlaybackByVideoId[selectedVideoId] ?? null;
  const selectedRecordingOption =
    recordingSelectionOptions.find(
      (recording) => recording.id === selectedRecording?.id,
    ) ?? null;
  const selectedRecordingPlaybackStatus = selectedRecording?.playbackStatus;
  const activeTimelineClip = useMemo(
    () => getVideoSessionClipAtTime(timeline, currentTime, selectedVideoId),
    [currentTime, selectedVideoId, timeline],
  );
  const loadedTimelineClip = useMemo(
    () =>
      timeline?.clips.find((clip) => clip.video.id === selectedVideoId) ?? null,
    [selectedVideoId, timeline],
  );
  const recordingMediaTimeline = useMemo(
    () =>
      selectedRecording
        ? buildRecordingMediaTimeline(selectedRecording, timeline)
        : null,
    [selectedRecording, timeline],
  );
  const finalRecordingChapter =
    recordingMediaTimeline?.chapters[
      recordingMediaTimeline.chapters.length - 1
    ] ?? null;
  const hlsRecordingSessionEnd = finalRecordingChapter?.sessionEnd ?? null;
  const hlsSourceUrl =
    recordingMediaTimeline && selectedRecording?.playbackUrl
      ? selectedRecording.playbackUrl
      : null;
  // On the HLS path the recording is the playback unit, so chapter 1's
  // thumbnail is the right poster. When a partial recording falls back to
  // per-file MP4 playback, the file's own thumbnail matches what will show.
  const posterUrl = hlsSourceUrl
    ? selectedRecording?.thumbnailUrl ?? selectedVideo?.thumbnailUrl ?? undefined
    : selectedVideo?.thumbnailUrl ?? undefined;
  const hlsPlayback = useHlsPlayer(videoRef, hlsSourceUrl);
  const { selectQuality } = hlsPlayback;
  const playback = useVideoPlaybackSource(selectedVideoId, hlsSourceUrl);
  // Stable setState identity, destructured so resolution-change callbacks
  // don't need the whole (per-render) playback object.
  const setSelectedSourceResolution = playback.setSelectedResolution;
  const playbackSourceId = hlsSourceUrl
    ? `recording:${selectedRecording?.id}`
    : `video:${selectedVideoId}`;
  const isSourceSwitchPending = hlsSourceUrl
    ? hlsPlayback.status !== "ready"
    : playback.isUrlSwitchPending;
  const isTimelineGap = timeline !== null && activeTimelineClip === null;
  // During a gap, keep the paused frame in the loaded clip's local time
  // domain instead of leaking the session-domain cursor value.
  const recordingPlaybackTime = getRecordingMediaTime(
    recordingMediaTimeline,
    currentTime,
    selectedVideoId,
  );
  const cursorPlaybackTime =
    recordingPlaybackTime ??
    (activeTimelineClip
      ? getVideoLocalTime(activeTimelineClip, currentTime)
      : loadedTimelineClip
        ? getVideoLocalTime(loadedTimelineClip, currentTime)
        : currentTime);
  const resolvedVideoDuration =
    videoDuration > 0
      ? videoDuration
      : recordingMediaTimeline?.mediaDuration ??
        activeTimelineClip?.duration ??
        selectedVideo?.duration ??
        0;
  const qualityMenu = useMemo<QualityMenuState>(() => {
    if (hlsSourceUrl) {
      return {
        levels: hlsPlayback.levels,
        activeHeight: hlsPlayback.activeHeight,
        isAuto: hlsPlayback.isAutoQuality,
        isLoading: hlsPlayback.status === "loading",
        allowAuto: true,
      };
    }

    // Legacy per-file MP4 renditions: each height is a distinct URL fetched
    // from the API, so there is no ABR ladder and therefore no Auto option.
    const levels = playback.availableResolutions
      .filter((option) => option.available)
      .map((option) => ({
        index: 0,
        height: Number.parseInt(option.resolution, 10),
        bitrate: option.bitrate ?? 0,
      }))
      .filter((level) => Number.isFinite(level.height) && level.height > 0)
      .sort((left, right) => right.height - left.height);

    const activeHeight = Number.parseInt(playback.selectedResolution, 10);
    return {
      levels,
      activeHeight: Number.isFinite(activeHeight) ? activeHeight : null,
      isAuto: false,
      isLoading: playback.isLoadingResolutions,
      allowAuto: false,
    };
  }, [
    hlsPlayback.activeHeight,
    hlsPlayback.isAutoQuality,
    hlsPlayback.levels,
    hlsPlayback.status,
    hlsSourceUrl,
    playback.availableResolutions,
    playback.isLoadingResolutions,
    playback.selectedResolution,
  ]);
  // Readout under the player. Keeps the bitrate detail that the old inline
  // dropdown label carried; the bar's own menu shows heights only.
  const qualityStatusLabel = [
    qualityMenu.isAuto
      ? qualityMenu.activeHeight
        ? t("pages:sessions.videoControls.autoWithHeight", {
            height: qualityMenu.activeHeight,
          })
        : t("pages:sessions.videoControls.auto")
      : qualityMenu.activeHeight !== null
        ? `${qualityMenu.activeHeight}p`
        : null,
    hlsSourceUrl && hlsPlayback.activeBitrate
      ? formatBitrate(hlsPlayback.activeBitrate)
      : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const videoCursorState = useMemo(
    () => getVideoCursorState(cursorPlaybackTime, resolvedVideoDuration),
    [cursorPlaybackTime, resolvedVideoDuration],
  );
  const syncStatusLabel = isSourceSwitchPending
    ? t("pages:sessions.syncPlayer.syncSwitching")
    : isTimelineGap
    ? t("pages:sessions.syncPlayer.syncNoVideo")
    : videoCursorState.isOutsideRange
    ? videoCursorState.position === "before"
      ? t("pages:sessions.syncPlayer.syncBeforeVideo")
      : t("pages:sessions.syncPlayer.syncBeyondVideo")
    : Math.abs(videoCurrentTime - cursorPlaybackTime) < 1
      ? t("pages:sessions.syncPlayer.syncSynced")
      : t("pages:sessions.syncPlayer.syncDrift");
  const isVideoSynced =
    !isSourceSwitchPending &&
    !isTimelineGap &&
    !videoCursorState.isOutsideRange &&
    Math.abs(videoCurrentTime - cursorPlaybackTime) < 1;
  // Latest-value mirrors for callbacks (handlePlayPause) that must observe
  // the newest render without re-arming. isPlayingRef is additionally
  // written directly so rapid toggle calls stay consistent between renders.
  const videoCursorStateRef = useLatest(videoCursorState);
  const isPlayingRef = useLatest(isPlaying);
  const currentTimeRef = useLatest(currentTime);
  const activeTimelineClipRef = useLatest(activeTimelineClip);
  // True while playback should continue across source switches; survives the
  // implicit pauses caused by clip changes and is cleared on user intent only.
  const playbackIntentRef = useRef(false);
  // Armed when a new source is loading; the cursor restore consumes it on the
  // first canplay so later canplay events (stalls, seeks) don't rewind.
  const pendingCursorRestoreRef = useRef(true);

  useEffect(() => {
    pendingCursorRestoreRef.current = true;
  }, [playback.playbackUrl, hlsSourceUrl, playbackSourceId]);

  // Deep-link cursor intent (issue #1003): the cursor-follow effect below
  // treats the clip under the shared cursor as authoritative, but the page
  // commits cursor moves on a debounce — so immediately after the one-shot
  // deep-link apply, the stale cursor (inside a different recording's clip)
  // would revert the deep-linked selection, and the revert's source reload
  // re-anchors the cursor, making the wrong selection stable. While this
  // intent is armed, cursor-driven switches defer to the deep-linked video.
  // It clears once the cursor reaches the deep-linked clip, when the
  // selection moves on, or shortly after arming (TTL) — whichever first.
  const pendingDeepLinkVideoIdRef = useRef<string | null>(null);
  const deepLinkCursorMoveRequestedRef = useRef(false);
  const deepLinkIntentTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Select the clip under the shared session cursor. During a recording gap,
  // keep the last source loaded so the player can show a stable paused frame.
  useEffect(() => {
    const activeVideoId = activeTimelineClip?.video.id;
    const nextVideoId =
      activeVideoId && selectableVideoIds.has(activeVideoId)
        ? activeVideoId
        : !selectedVideoId || !selectableVideoIds.has(selectedVideoId)
          ? isRecordingSelection
            ? recordingSelectionOptions[0]?.videoIds[0]
            : timeline?.clips[0]?.video.id ?? videos[0]?.id
          : null;

    if (nextVideoId && nextVideoId !== selectedVideoId) {
      // While a deep-link intent is armed for the current selection, the
      // deep-linked video outranks the clip under the (still-debounced)
      // shared cursor — see pendingDeepLinkVideoIdRef. Suppressed only for
      // cursor-driven switches; invalid-selection recovery still applies.
      const isCursorDriven = activeVideoId === nextVideoId;
      const deepLinkIntentVideoId = pendingDeepLinkVideoIdRef.current;
      const intentHoldsSelection =
        deepLinkIntentVideoId != null &&
        selectedVideoId === deepLinkIntentVideoId;
      if (!(isCursorDriven && intentHoldsSelection)) {
        setSelectedVideoId(nextVideoId);
        onVideoSelect?.(nextVideoId);
      }
    }
  }, [
    activeTimelineClip,
    isRecordingSelection,
    onVideoSelect,
    recordingSelectionOptions,
    selectableVideoIds,
    selectedVideoId,
    timeline,
    videos,
  ]);

  useEffect(() => {
    setVideoDuration(0);
  }, [playbackSourceId]);

  // Sync video time with the shared cursor when currentTime prop changes.
  useEffect(() => {
    const targetVideoTime = videoCursorState.playbackTime;

    if (videoRef.current && isTimelineGap && !isUserSeeking) {
      videoRef.current.pause();
      playbackIntentRef.current = false;
      setIsPlaying(false);
      setLastSyncTime(currentTime);
      return;
    }

    // While the element still holds the previous clip's media, don't seek it
    // to the incoming clip's local times; the restore after load handles it.
    if (isSourceSwitchPending) {
      return;
    }

    if (
      videoRef.current &&
      !isUserSeeking &&
      currentTime !== undefined &&
      (Math.abs(videoRef.current.currentTime - targetVideoTime) >
        DRIFT_THRESHOLD_S ||
        Math.abs(lastSyncTime - currentTime) > DRIFT_THRESHOLD_S)
    ) {
      // Clear any pending retry
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }

      setIntendedTime(targetVideoTime);
      if (
        Math.abs(videoRef.current.currentTime - targetVideoTime) >
        DRIFT_THRESHOLD_S
      ) {
        videoRef.current.currentTime = targetVideoTime;
      }
      setVideoCurrentTime(targetVideoTime);
      if (videoCursorState.isOutsideRange) {
        videoRef.current.pause();
        playbackIntentRef.current = false;
        setIsPlaying(false);
      }
      setLastSyncTime(currentTime);

      // Set up a retry in case the video resets
      retryTimeoutRef.current = setTimeout(() => {
        if (
          videoRef.current &&
          Math.abs(videoRef.current.currentTime - targetVideoTime) >
            RETRY_SEEK_TOLERANCE_S
        ) {
          videoRef.current.currentTime = targetVideoTime;
        }
      }, RETRY_DELAY_MS);
    }
  }, [
    currentTime,
    isSourceSwitchPending,
    isTimelineGap,
    isUserSeeking,
    lastSyncTime,
    selectedVideoId,
    videoCursorState,
  ]);

  // Cleanup retry timeout on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (deepLinkIntentTimeoutRef.current) {
        clearTimeout(deepLinkIntentTimeoutRef.current);
      }
    };
  }, []);

  // Sync video volume with prop
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = volume;
      setIsMuted(volume === 0);
    }
  }, [volume]);

  const playbackTimeToSessionTime = useCallback(
    (playbackTime: number): number | null => {
      if (recordingMediaTimeline) {
        const normalizedPlaybackTime =
          selectedRecordingPlaybackStatus === "partial" &&
          Number.isFinite(videoDuration) &&
          videoDuration > 0 &&
          playbackTime >= videoDuration - TIMELINE_EPSILON_SECONDS
            // Stay outside the next chapter's epsilon window when a partial
            // playlist ends exactly on a chapter boundary.
            ? Math.max(
                0,
                videoDuration - TIMELINE_EPSILON_SECONDS * 2,
              )
            : playbackTime;
        return getRecordingSessionTime(
          recordingMediaTimeline,
          normalizedPlaybackTime,
        );
      }
      return activeTimelineClip
        ? getVideoSessionTime(activeTimelineClip, playbackTime)
        : playbackTime;
    },
    [
      activeTimelineClip,
      recordingMediaTimeline,
      selectedRecordingPlaybackStatus,
      videoDuration,
    ],
  );

  const handleVideoSelect = useCallback((videoId: string) => {
    setSelectedVideoId(videoId);
    onVideoSelect?.(videoId);
    playbackIntentRef.current = false;
    setIsPlaying(false);

    // When the chosen clip doesn't cover the shared cursor, move the cursor
    // into the clip so the auto-switch effect doesn't immediately revert.
    if (timeline && getVideoSessionClipAtTime(timeline, currentTime, videoId)?.video.id !== videoId) {
      const selectedClip = timeline.clips.find(
        (clip) => clip.video.id === videoId,
      );
      if (selectedClip) {
        onTimeUpdate?.(selectedClip.startTime);
      }
    }
  }, [onVideoSelect, timeline, currentTime, onTimeUpdate]);

  const handleRecordingSelect = (recordingId: string) => {
    const recording = recordingSelectionOptions.find(
      (option) => option.id === recordingId,
    );
    const videoId = recording?.videoIds[0];
    if (videoId) {
      handleVideoSelect(videoId);
    }
  };

  // One-shot deep-link application: guarded by a ref so a still-present
  // requestedVideoId never reverts a later manual selection (see prop docs).
  const appliedRequestedVideoIdRef = useRef<string | null>(null);

  // Deep-link intent lifecycle: request the cursor move once the timeline
  // exists (if not yet requested), and clear the intent when the cursor
  // reaches the deep-linked clip or the selection has moved on. Declared
  // BEFORE the arming effect so it never evaluates in the arm's own commit
  // (where the render-scope selection still predates the apply).
  useEffect(() => {
    const pendingVideoId = pendingDeepLinkVideoIdRef.current;
    if (!pendingVideoId) {
      return;
    }
    if (selectedVideoId !== pendingVideoId) {
      pendingDeepLinkVideoIdRef.current = null;
      return;
    }
    if (activeTimelineClip?.video.id === pendingVideoId) {
      pendingDeepLinkVideoIdRef.current = null;
      return;
    }
    if (timeline && !deepLinkCursorMoveRequestedRef.current) {
      const pendingClip = timeline.clips.find(
        (clip) => clip.video.id === pendingVideoId,
      );
      if (pendingClip) {
        deepLinkCursorMoveRequestedRef.current = true;
        onTimeUpdate?.(pendingClip.startTime);
      }
    }
  }, [timeline, selectedVideoId, activeTimelineClip, onTimeUpdate]);

  useEffect(() => {
    if (
      !requestedVideoId ||
      appliedRequestedVideoIdRef.current === requestedVideoId
    ) {
      return;
    }
    if (!selectableVideoIds.has(requestedVideoId)) {
      return;
    }
    appliedRequestedVideoIdRef.current = requestedVideoId;
    handleVideoSelect(requestedVideoId);
    pendingDeepLinkVideoIdRef.current = requestedVideoId;
    // handleVideoSelect already requested the cursor move when the timeline
    // exists; otherwise the consumer above requests it once it materializes.
    deepLinkCursorMoveRequestedRef.current = timeline != null;
    if (deepLinkIntentTimeoutRef.current) {
      clearTimeout(deepLinkIntentTimeoutRef.current);
    }
    deepLinkIntentTimeoutRef.current = setTimeout(() => {
      pendingDeepLinkVideoIdRef.current = null;
    }, DEEP_LINK_INTENT_TTL_MS);
  }, [requestedVideoId, selectableVideoIds, handleVideoSelect, timeline]);

  const handleResolutionChange = useCallback(
    (resolution: string) => {
      setSelectedSourceResolution(resolution);
      if (hlsSourceUrl) {
        selectQuality(
          resolution === "auto"
            ? null
            : Number.parseInt(resolution.replace("p", ""), 10),
        );
        return;
      }
      // Preserve current time when switching resolution
      const currentTime = videoRef.current?.currentTime || 0;
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.currentTime = currentTime;
        }
      }, 100);
    },
    [hlsSourceUrl, selectQuality, setSelectedSourceResolution],
  );

  // VideoControlBar speaks in rendition heights (null meaning adaptive); the
  // legacy MP4 path is still addressed by its "360p"-style resolution string.
  const handleQualityChange = useCallback(
    (height: number | null) => {
      handleResolutionChange(height === null ? "auto" : `${height}p`);
    },
    [handleResolutionChange],
  );

  const handleHlsRetry = async () => {
    if (!selectedRecording || !onRefreshRecordingPlayback) {
      hlsPlayback.retry();
      return;
    }

    try {
      const refreshedUrl = await onRefreshRecordingPlayback(
        selectedRecording.id,
      );
      // A changed URL causes useHlsPlayer to reattach automatically. If the
      // backend returned the same URL, explicitly reload it for transient
      // failures that are unrelated to token expiry.
      if (!refreshedUrl || refreshedUrl === hlsSourceUrl) {
        hlsPlayback.retry();
      }
    } catch (error) {
      console.error("Error refreshing recording playback URL:", error);
    }
  };

  const handlePlayPause = useCallback(() => {
    if (videoRef.current) {
      const cursorState = videoCursorStateRef.current;
      const playing = isPlayingRef.current;

      // Pressing play in a gap or at the end of the active clip's coverage
      // continues the session at the next recorded clip instead of dead-ending.
      if (!playing) {
        const sessionTime = currentTimeRef.current;
        const activeClip = activeTimelineClipRef.current;
        const atHlsRecordingEnd =
          hlsSourceUrl !== null &&
          hlsRecordingSessionEnd !== null &&
          sessionTime >=
            hlsRecordingSessionEnd - TIMELINE_EPSILON_SECONDS;
        const atLegacyClipEnd =
          hlsSourceUrl === null &&
          activeClip !== null &&
          sessionTime >=
            activeClip.endTime - TIMELINE_EPSILON_SECONDS;
        const atCoverageEnd =
          isTimelineGap ||
          cursorState.isOutsideRange ||
          atHlsRecordingEnd ||
          atLegacyClipEnd;

        if (atCoverageEnd) {
          const nextClip = getNextVideoSessionClip(timeline, sessionTime);
          if (nextClip) {
            playbackIntentRef.current = true;
            onTimeUpdate?.(nextClip.startTime);
            return;
          }
        }
      }

      if (isTimelineGap || cursorState.isOutsideRange) {
        videoRef.current.currentTime = cursorState.playbackTime;
        videoRef.current.pause();
        playbackIntentRef.current = false;
        isPlayingRef.current = false;
        setIsPlaying(false);
        return;
      }

      if (playing) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }

      playbackIntentRef.current = !playing;
      isPlayingRef.current = !playing;
      setIsPlaying(!playing);
    }
  }, [
    activeTimelineClipRef,
    currentTimeRef,
    hlsRecordingSessionEnd,
    hlsSourceUrl,
    isPlayingRef,
    isTimelineGap,
    onTimeUpdate,
    timeline,
    videoCursorStateRef,
  ]);

  // Unconditional pause (never plays), with the internal playing state kept
  // coherent. Called from the parent when leaving the page and on history
  // traversals below: a playing media keeps firing timeupdate-driven state
  // updates that can indefinitely starve React Router's transition-based
  // route swap, leaving the old screen on display while the URL changes.
  const pausePlayback = useCallback(() => {
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
      playbackIntentRef.current = false;
      isPlayingRef.current = false;
      setIsPlaying(false);
    }
  }, [isPlayingRef]);

  // Browser back/forward while this player is mounted: pause immediately so
  // the timeupdate stream cannot preempt the router's startTransition-wrapped
  // update (issue #1151: back navigation changed the URL but the analytics
  // screen stayed on display).
  useEffect(() => {
    window.addEventListener("popstate", pausePlayback);
    return () => window.removeEventListener("popstate", pausePlayback);
  }, [pausePlayback]);

  const handleTimeUpdate = () => {
    if (videoRef.current && !isUserSeeking) {
      // Events from the previous clip's media must not move the session
      // cursor: mapping them through the new clip would leap the timeline.
      if (isSourceSwitchPending) {
        return;
      }

      const time = videoRef.current.currentTime;
      setVideoCurrentTime(time);

      if (isTimelineGap || videoCursorState.isOutsideRange) {
        setIntendedTime(videoCursorState.playbackTime);
        setLastSyncTime(currentTime);
        return;
      }

      // Don't call onTimeUpdate if video unexpectedly resets
      // Check if the current time is very different from what we intended
      const timeDifference = Math.abs(time - intendedTime);
      if (time === 0 && intendedTime > UNEXPECTED_RESET_MIN_INTENDED_S) {
        // Try to restore the intended time
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.currentTime = intendedTime;
          }
        }, RESET_RESTORE_DELAY_MS);
        return;
      }

      // If time is significantly different from intended, don't propagate the change
      // But allow small progressive changes during normal playback
      if (
        timeDifference > SEEK_JUMP_THRESHOLD_S &&
        intendedTime > SEEK_JUMP_THRESHOLD_S &&
        Math.abs(time - videoCurrentTime) > PROPAGATION_JUMP_S
      ) {
        return;
      }

      // Update intended time during normal playback (small progressive changes)
      if (timeDifference <= PROGRESSIVE_UPDATE_TOLERANCE_S) {
        setIntendedTime(time);
      }

      const sessionTime = playbackTimeToSessionTime(time);
      if (sessionTime === null) {
        return;
      }
      onTimeUpdate?.(sessionTime);
    }
  };

  const restoreLoadedVideoCursor = (consumePendingRestore = false) => {
    if (
      !videoRef.current ||
      isUserSeeking ||
      isTimelineGap ||
      isSourceSwitchPending ||
      !pendingCursorRestoreRef.current
    ) {
      return;
    }

    const duration = videoRef.current.duration;
    const targetTime = Math.min(
      Math.max(cursorPlaybackTime, 0),
      Number.isFinite(duration) ? duration : cursorPlaybackTime,
    );
    videoRef.current.currentTime = targetTime;
    setVideoCurrentTime(targetTime);
    setIntendedTime(targetTime);
    setLastSyncTime(currentTime);

    if (consumePendingRestore) {
      pendingCursorRestoreRef.current = false;
    }

    if (playbackIntentRef.current && videoRef.current.paused) {
      videoRef.current.play();
      isPlayingRef.current = true;
      setIsPlaying(true);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current && selectedVideoId && !isSourceSwitchPending) {
      const duration = videoRef.current.duration;
      setVideoDuration(duration);
      if (!hlsSourceUrl) {
        onDurationChange?.(selectedVideoId, duration);
      }
      restoreLoadedVideoCursor();
    }
  };

  const handleDurationChange = () => {
    if (
      hlsSourceUrl &&
      videoRef.current &&
      Number.isFinite(videoRef.current.duration) &&
      videoRef.current.duration > 0
    ) {
      setVideoDuration(videoRef.current.duration);
    }
  };

  const handleSeekStart = useCallback(
    (
      e:
        | React.MouseEvent<HTMLInputElement>
        | React.TouchEvent<HTMLInputElement>,
    ) => {
      setIsUserSeeking(true);
      setIsDraggingScrubber(false);
      const point = "touches" in e ? e.touches[0] : e;
      if (point) {
        setInitialMousePos({ x: point.clientX, y: point.clientY });
      }
    },
    [],
  );

  const handleSeekEnd = useCallback(() => {
    setIsUserSeeking(false);

    if (videoRef.current && isDraggingScrubber && pendingSeekTime !== null) {
      const actualTime = videoRef.current.currentTime;

      // Use the pending time if video time was reset
      const timeToUse =
        Math.abs(actualTime - pendingSeekTime) > 1
          ? pendingSeekTime
          : actualTime;

      if (timeToUse !== actualTime) {
        videoRef.current.currentTime = timeToUse;
      }

      setVideoCurrentTime(timeToUse);
      setIntendedTime(timeToUse);
      setPendingSeekTime(null);
      const sessionTime = playbackTimeToSessionTime(timeToUse);
      if (sessionTime === null) {
        setIsDraggingScrubber(false);
        setInitialMousePos(null);
        return;
      }
      setLastSyncTime(sessionTime);

      // Delay the sync state update to prevent immediate feedback
      setTimeout(() => {
        onTimeUpdate?.(sessionTime);
      }, 50);
    }

    setIsDraggingScrubber(false);
    setInitialMousePos(null);
  }, [
    isDraggingScrubber,
    onTimeUpdate,
    pendingSeekTime,
    playbackTimeToSessionTime,
  ]);

  const handleSeekMove = useCallback(
    (e: React.MouseEvent<HTMLInputElement>) => {
      if (isUserSeeking && initialMousePos && !isDraggingScrubber) {
        const distance = Math.sqrt(
          Math.pow(e.clientX - initialMousePos.x, 2) +
            Math.pow(e.clientY - initialMousePos.y, 2),
        );

        // Start dragging if mouse moved more than 3 pixels
        if (distance > 3) {
          setIsDraggingScrubber(true);
        }
      }
    },
    [initialMousePos, isDraggingScrubber, isUserSeeking],
  );

  const handleSeek = useCallback((time: number) => {
    // Only update video time if we're actively dragging
    if (isDraggingScrubber) {
      setPendingSeekTime(time);
      if (videoRef.current) {
        videoRef.current.currentTime = time;
        setVideoCurrentTime(time);
        // Don't update sync during drag - wait for mouse up
      }
    }
  }, [isDraggingScrubber]);

  const handleVolumeChange = useCallback(
    (newVolume: number) => {
      onVolumeChange?.(newVolume);
      if (videoRef.current) {
        videoRef.current.volume = newVolume;
      }
      setIsMuted(newVolume === 0);
    },
    [onVolumeChange],
  );

  const toggleMute = useCallback(() => {
    if (videoRef.current) {
      const newMuted = !isMuted;
      videoRef.current.muted = newMuted;
      setIsMuted(newMuted);
    }
  }, [isMuted]);

  // Expose video controls to the hosting page. SessionAnalyticsPage wires
  // them to its document-level keyboard shortcuts (issue #1206).
  useEffect(() => {
    if (onGetVideoControls) {
      onGetVideoControls({
        togglePlayPause: handlePlayPause,
        pause: pausePlayback,
        toggleMute,
        toggleFullscreen,
      });
    }
  }, [
    onGetVideoControls,
    handlePlayPause,
    pausePlayback,
    toggleMute,
    toggleFullscreen,
  ]);

  const handleRestart = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      onTimeUpdate?.(
        recordingMediaTimeline?.chapters[0]?.sessionStart ??
          activeTimelineClip?.startTime ??
          0,
      );
    }
  }, [activeTimelineClip, onTimeUpdate, recordingMediaTimeline]);

  const formatTime = (time: number) => {
    if (!Number.isFinite(time)) {
      return "0:00";
    }

    const sign = time < 0 ? "-" : "";
    const absoluteTime = Math.abs(time);
    const minutes = Math.floor(absoluteTime / 60);
    const seconds = Math.floor(absoluteTime % 60);
    return `${sign}${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  // Both source-picker render sites (expanded header and compact overlay)
  // share the same options, value, and change handling.
  const sourceSelectOptions = isRecordingSelection
    ? recordingSelectionOptions.map((recording) => ({
        value: recording.id,
        label: recording.label,
      }))
    : videos.map((video) => ({ value: video.id, label: video.filename }));
  const sourceSelectValue = isRecordingSelection
    ? selectedRecordingOption?.id ?? ""
    : selectedVideoId;
  const handleSourceSelectChange = (videoId: string) =>
    isRecordingSelection
      ? handleRecordingSelect(videoId)
      : handleVideoSelect(videoId);
  const sourceSelectAriaLabel = isRecordingSelection
    ? t("pages:sessions.syncPlayer.ariaSelectRecording")
    : t("pages:sessions.syncPlayer.ariaSelectVideo");
  const collapseButtonAriaLabel = isCollapsed
    ? t("pages:sessions.syncPlayer.ariaExpandPlayer")
    : t("pages:sessions.syncPlayer.ariaCollapsePlayer");
  const showSourceSelect =
    (isRecordingSelection ? recordingSelectionOptions.length : videos.length) >
    1;

  if (
    videos.length === 0 ||
    (isRecordingSelection && recordingSelectionOptions.length === 0)
  ) {
    const emptyTitle =
      videos.length > 0
        ? t("sessions.detail.videoFiles.preparingRecording")
        : t("pages:sessions.syncPlayer.emptyNoVideos");
    const emptyBody =
      videos.length > 0
        ? t("sessions.detail.videoFiles.preparingRecordingBody")
        : t("pages:sessions.syncPlayer.emptyUploadHint");
    if (isCompact) {
      return (
        <div
          className={`flex min-h-48 items-center justify-center rounded-lg bg-[#0d0d14] p-6 text-center text-[#4b5563] ${className}`}
        >
          <div>
            <p>{emptyTitle}</p>
            <p className="mt-1 text-sm text-[#374151]">
              {emptyBody}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div
 className={`flex flex-col bg-[#0d0d14] border border-[#1a1a2e] ${className}`}
      >
 <div className="p-4 border-b border-[#1a1a2e] bg-[#111827]">
 <div className="flex items-center justify-between">
 <h3 className="text-base font-semibold text-slate-200">
              {t("pages:sessions.syncPlayer.sessionVideo")}
            </h3>
            {onToggleCollapse && (
              <CollapseButton
                isCollapsed={isCollapsed}
                onToggle={onToggleCollapse}
                ariaLabel={collapseButtonAriaLabel}
              />
            )}
          </div>
        </div>
        {!isCollapsed && (
          <div className="flex-1 p-6 text-center text-[#4b5563]">
            <p>{emptyTitle}</p>
 <p className="text-sm mt-1 text-[#374151]">
              {emptyBody}
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
 className={`flex flex-col ${
       isCompact ? "" : "bg-[#0d0d14] border border-[#1a1a2e]"
     } ${className}`}
    >
      {!isCompact && (
 <div className="p-4 border-b border-[#1a1a2e] bg-[#111827] flex-shrink-0">
 <div className="flex items-center justify-between">
 <h3 className="text-base font-semibold text-slate-200">{t("pages:sessions.syncPlayer.sessionVideo")}</h3>
 <div className="flex items-center space-x-2">
            {(timeline?.clips.length ?? 0) > 1 && (
              <span className="inline-flex items-center gap-1.5 text-xs text-[#4ade80]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#22c55e] shadow-[0_0_4px_#22c55e]" />
                {t("pages:sessions.syncPlayer.autoSwitch")}
              </span>
            )}
            {showSourceSelect && (
              <VideoSourceSelect
                ariaLabel={sourceSelectAriaLabel}
                value={sourceSelectValue}
                onChange={handleSourceSelectChange}
                options={sourceSelectOptions}
                className="text-sm border border-[#1f2937] bg-[#0d0d14] text-slate-200 rounded px-2 py-1"
              />
            )}
            {onToggleCollapse && (
              <CollapseButton
                isCollapsed={isCollapsed}
                onToggle={onToggleCollapse}
                ariaLabel={collapseButtonAriaLabel}
              />
            )}
          </div>
        </div>
      </div>
      )}

      {!isCollapsed && selectedVideo && (
 <div
          className={`flex-1 min-h-0 overflow-auto ${isCompact ? "" : "p-4"}${
            fillAvailableSpace ? " flex flex-col" : ""
          }`}
        >
          {selectedRecordingPlaybackStatus === "partial" && (
            <div
              role="status"
              className="mb-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-amber-100"
            >
              <AlertTriangle
                className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-400"
                aria-hidden="true"
              />
              <div>
                <p className="text-sm font-medium">
                  {t("sessions.detail.videoFiles.incompleteRecordingTitle")}
                </p>
                <p className="mt-0.5 text-xs text-amber-200/80">
                  {t("sessions.detail.videoFiles.incompleteRecordingBody")}
                </p>
              </div>
            </div>
          )}
          <div
            ref={playerShellRef}
            className={`video-player-shell relative bg-black rounded-lg overflow-hidden border border-[#1a1a2e]${
              fillAvailableSpace ? " flex-1 min-h-0" : ""
            }`}
            onMouseEnter={() => setShowControls(true)}
            onMouseLeave={() => setShowControls(false)}
          >
            {isCompact && showSourceSelect && (
              <VideoSourceSelect
                ariaLabel={sourceSelectAriaLabel}
                value={sourceSelectValue}
                onChange={handleSourceSelectChange}
                options={sourceSelectOptions}
                className="absolute right-3 top-3 z-20 max-w-[calc(100%-1.5rem)] rounded border border-white/20 bg-black/75 px-2 py-1 text-sm text-white backdrop-blur-sm"
              />
            )}
            <video
              ref={videoRef}
              poster={posterUrl}
              src={
                hlsSourceUrl
                  ? undefined
                  : playback.playbackUrl || selectedVideo?.url
              }
              className={`${
                fillAvailableSpace ? "h-full" : "h-auto max-h-80"
              } w-full object-contain transition-opacity ${
                isTimelineGap ? "opacity-20" : "opacity-100"
              }`}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onDurationChange={handleDurationChange}
              onCanPlay={() => restoreLoadedVideoCursor(true)}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              preload="metadata"
            />

            {hlsSourceUrl && hlsPlayback.status === "error" && (
              <div
                role="alert"
                className="absolute inset-0 z-30 flex items-center justify-center bg-[#020617]/90 px-6 text-center"
              >
                <div>
                  <AlertTriangle
                    className="mx-auto h-7 w-7 text-amber-400"
                    aria-hidden="true"
                  />
                  <p className="mt-2 text-sm font-medium text-slate-100">
                    {t("sessions.detail.videoFiles.hlsPlaybackErrorTitle")}
                  </p>
                  <p className="mt-1 text-xs text-slate-400">
                    {hlsPlayback.engine === "unsupported"
                      ? t(
                          "sessions.detail.videoFiles.hlsUnsupportedBrowser",
                        )
                      : t(
                          "sessions.detail.videoFiles.hlsPlaybackErrorBody",
                        )}
                  </p>
                  {hlsPlayback.engine !== "unsupported" && (
                    <button
                      type="button"
                      onClick={handleHlsRetry}
                      className="mt-3 rounded border border-slate-500 px-3 py-1.5 text-xs font-medium text-slate-100 hover:bg-slate-800"
                    >
                      {t("sessions.detail.videoFiles.retryPlayback")}
                    </button>
                  )}
                </div>
              </div>
            )}

            {isTimelineGap && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#020617]/70 text-center">
                <div>
                  <p className="text-sm font-medium text-slate-200">
                    {t("pages:sessions.syncPlayer.noVideoAtTime")}
                  </p>
                  <p className="mt-1 text-xs text-[#64748b]">
                    {t("pages:sessions.syncPlayer.telemetryInGap")}
                  </p>
                </div>
              </div>
            )}

            <VideoControlBar
              isPlaying={isPlaying}
              currentTime={videoCurrentTime}
              duration={resolvedVideoDuration}
              bufferedEnd={bufferedEnd}
              volume={volume}
              isMuted={isMuted}
              playbackRate={playbackRate}
              isFullscreen={isFullscreen}
              pip={{ supported: pip.isSupported, active: pip.isActive }}
              quality={qualityMenu}
              disabled={
                isTimelineGap ||
                (Boolean(hlsSourceUrl) && hlsPlayback.status !== "ready")
              }
              variant={variant}
              visible={showControls}
              extraControls={
                <button
                  type="button"
                  onClick={handleRestart}
                  title={t("pages:sessions.syncPlayer.titleRestart")}
                  aria-label={t("pages:sessions.syncPlayer.titleRestart")}
                  className="rounded p-2 text-white transition-colors hover:text-[#60a5fa]"
                >
                  <RotateCcw className="h-4 w-4" aria-hidden="true" />
                </button>
              }
              onPlayPause={handlePlayPause}
              onSeek={handleSeek}
              onSeekStart={handleSeekStart}
              onSeekMove={handleSeekMove}
              onSeekEnd={handleSeekEnd}
              onVolumeChange={handleVolumeChange}
              onToggleMute={toggleMute}
              onPlaybackRateChange={setPlaybackRate}
              onQualityChange={handleQualityChange}
              onToggleFullscreen={toggleFullscreen}
              onTogglePip={pip.togglePip}
            />
          </div>

          {/* Sync Status */}
          {!isCompact && (
 <div className="mt-3 text-sm text-[#94a3b8]">
 <div className="flex items-center justify-between">
              <span>
                {t("pages:sessions.syncPlayer.labelVideoTime")} {formatTime(videoCurrentTime)} / {timeline ? t("pages:sessions.syncPlayer.labelSessionTime") : t("pages:sessions.syncPlayer.labelCursorTime")}:{" "}
                {formatTime(currentTime)}
              </span>
 <div className="flex items-center space-x-2 text-xs">
 <span className="text-[#64748b]">
                  {qualityStatusLabel}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className={`h-2 w-2 rounded-full ${
                      isVideoSynced
                        ? "bg-[#22c55e] shadow-[0_0_6px_#22c55e]"
                        : "bg-[#facc15] shadow-[0_0_6px_#facc15]"
                    }`}
                  />
                  {syncStatusLabel}
                </span>
              </div>
            </div>
          </div>
          )}

          {/* Video Synchronization Controls */}
          {!isCompact && onSyncOffsetChange && (
 <div className="mt-3 border-t border-[#1a1a2e] pt-3">
 <div className="flex items-center justify-between mb-2">
                <button
                  onClick={() =>
                    setIsSyncControlsCollapsed(!isSyncControlsCollapsed)
                  }
 className="flex items-center text-sm font-medium text-slate-300 hover:text-white transition-colors"
                >
  <span className="mr-1">{t("pages:sessions.syncPlayer.syncControls")}</span>
                  {isSyncControlsCollapsed ? (
 <ChevronDown className="h-3 w-3" />
                  ) : (
 <ChevronUp className="h-3 w-3" />
                  )}
                </button>
              </div>

              {!isSyncControlsCollapsed && (
                <VideoTelemetrySyncOffset
                  syncOffset={syncOffset}
                  onSyncOffsetChange={onSyncOffsetChange}
                  videoCreatedAt={videoCreatedAt}
                  telemetryStartTime={telemetryStartTime}
                  telemetryTimestampSource={telemetryTimestampSource}
 className="border-0"
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
